import * as THREE from 'three'
import { ProtoBase } from '../ProtoBase'
import { SITES, MITAKA_XZ, toXZ, type KanjinSite } from '../kanjinData'
import type { SfxKind } from '../shared/Sfx'

/** ④ 方位の立体音響 — 各地の点灯と同時に、その方角から音が鳴る（要ヘッドホン） */
export class Proto4 extends ProtoBase {
  readonly title = '4. 方位の立体音響'
  readonly desc = '寄進地が西から順に灯り、その方角から音が届く（ヘッドホン推奨）'

  private order: KanjinSite[] = []
  private idx = -1
  private stepT = 0
  private static readonly STEP = 1.9

  protected start(): void {
    this.ctx.setWorld('map')
    // 三鷹を中心に据えた視点（方位が音と一致するように北=奥）
    this.ctx.view.setPose(
      new THREE.Vector3(MITAKA_XZ.x, 55, MITAKA_XZ.z + 60),
      new THREE.Vector3(MITAKA_XZ.x * 0.4, 0, MITAKA_XZ.z * 0.4),
    )
    this.order = [...SITES].sort((a, b) => a.lon - b.lon) // 西→東
    this.ctx.setCaption('耳を澄ませて——西から順に、寄進の音が届く（ヘッドホン推奨）')
    this.stepT = Proto4.STEP * 0.7
  }

  private soundFor(site: KanjinSite): SfxKind {
    if (site.no === 34) return 'drum' // 鶴岡八幡=鼓
    if (site.no === 16) return 'drum'
    if (site.kind === 'temple') return 'bell'
    if (site.lat < 34 || site.no === 4 || site.no === 54) return 'wave' // 海辺の社
    return 'chime'
  }

  private dirLabel(site: KanjinSite): string {
    const dx = site.lon - 139.523
    const dy = site.lat - 35.688
    const ang = (Math.atan2(dy, dx) * 180) / Math.PI // 東=0 反時計
    const dirs: [number, string][] = [
      [0, '東'], [45, '北東'], [90, '北'], [135, '北西'], [180, '西'], [-180, '西'], [-135, '南西'], [-90, '南'], [-45, '南東'],
    ]
    let best = '西'
    let bestD = 999
    for (const [a, name] of dirs) {
      const d = Math.abs(ang - a)
      if (d < bestD) {
        bestD = d
        best = name
      }
    }
    return best
  }

  update(dt: number): void {
    this.elapsed += dt
    this.stepT += dt
    if (this.stepT >= Proto4.STEP) {
      this.stepT = 0
      this.idx = (this.idx + 1) % this.order.length
      if (this.idx === 0) this.ctx.markers.clearHighlights()
      const site = this.order[this.idx]
      const p = toXZ(site.lat, site.lon)
      const camRel = new THREE.Vector3(p.x, 0, p.z).sub(this.ctx.controls.target)
      this.ctx.sfx.play(this.soundFor(site), camRel)
      this.ctx.markers.highlight(site.no, 1)
      this.ctx.setCaption(`${this.dirLabel(site)}から——${site.building}「${site.piece}」`)
    }
    // 直近ハイライトの減衰
    this.order.forEach((s, i) => {
      if (i !== this.idx) return
      this.ctx.markers.highlight(s.no, 0.5 + 0.5 * Math.max(0, 1 - this.stepT))
    })
  }

  protected onDispose(): void {
    this.ctx.markers.clearHighlights()
  }
}
